/* ═══════════════════════════════════════════════════
   DASHBOARD EVENTS — week strip

   Deliberately has no events of its own. It fetches the Events page and reads
   the very same JSON blob the calendar renders from, so events are managed in
   exactly one place and can never drift between the two pages.

   Note there is no timezone maths here, unlike events-calendar.js. Grouping
   only needs the plain YYYY-MM-DD date, and the time is shown exactly as
   typed ("8:00 PM EDT"), so this doesn't duplicate that logic.
   ═══════════════════════════════════════════════════ */

(function () {
  var DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var CATEGORY_COLORS = {
    stream: '--de-color-stream',
    community: '--de-color-community',
    new: '--de-color-new'
  };

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function dayKey(date) {
    var m = date.getMonth() + 1;
    var d = date.getDate();
    return date.getFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (d < 10 ? '0' + d : d);
  }

  function categoryColor(root, category) {
    var name = CATEGORY_COLORS[category] || CATEGORY_COLORS.stream;
    var value = getComputedStyle(root).getPropertyValue(name);
    return value ? value.trim() : '#a855f7';
  }

  /* Weekly events are one block on the Events page; spread them over the weeks (same rule as
     events-calendar.js expandRepeats: from Date, every 7 days, until Repeat until or 400 days out). */
  function expandRepeats(list) {
    var out = [];
    var horizon = new Date();
    horizon.setHours(0, 0, 0, 0);
    horizon.setDate(horizon.getDate() + 400);
    list.forEach(function (ev) {
      if (ev.repeat !== 'weekly') { out.push(ev); return; }
      var p = String(ev.date).split('-');
      var d = new Date(+p[0], +p[1] - 1, +p[2]);
      if (isNaN(d.getTime())) return;
      var end = horizon;
      if (ev.repeat_until) {
        var q = String(ev.repeat_until).split('-');
        var u = new Date(+q[0], +q[1] - 1, +q[2]);
        if (!isNaN(u.getTime()) && u < horizon) end = u;
      }
      for (var n = 0; d <= end && n < 600; n++) {
        var copy = {};
        for (var k in ev) if (Object.prototype.hasOwnProperty.call(ev, k)) copy[k] = ev[k];
        copy.date = dayKey(d);
        copy.id = ev.id + '-' + copy.date;
        out.push(copy);
        d.setDate(d.getDate() + 7);
      }
    });
    return out;
  }

  /* Pull the calendar's own JSON out of the Events page markup. */
  function loadEvents(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Events page returned ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var tag = doc.querySelector('[data-ec-events]');
        if (!tag) throw new Error('No event data on the Events page');
        var parsed = JSON.parse(tag.textContent) || [];
        return expandRepeats(parsed.filter(function (ev) {
          return ev && ev.title && ev.date;
        }));
      });
  }

  function initDashEvents(root) {
    var weekEl = root.querySelector('[data-de-week]');
    var listEl = root.querySelector('[data-de-list]');
    if (!weekEl || !listEl) return;

    var source = root.getAttribute('data-de-source') || '/pages/events';
    var days = parseInt(root.getAttribute('data-de-days'), 10);
    if (isNaN(days) || days < 1) days = 7;

    loadEvents(source)
      .then(function (events) {
        if (!events.length) {
          root.hidden = true;
          return;
        }
        render(root, weekEl, listEl, events, days, source);
        bindToggle(root, weekEl, listEl);
        root.hidden = false;
      })
      .catch(function (err) {
        /* A member who can't see the Events page (or a network hiccup) just
           doesn't get this section, rather than an error on their dashboard. */
        root.hidden = true;
        console.warn('[dashboard-events]', err.message);
      });
  }

  function render(root, weekEl, listEl, events, days, source) {
    var byDay = {};
    events.forEach(function (ev) {
      if (!byDay[ev.date]) byDay[ev.date] = [];
      byDay[ev.date].push(ev);
    });

    /* ── Week strip: today plus the next N-1 days ── */
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var columns = [];

    for (var i = 0; i < days; i++) {
      var date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
      var key = dayKey(date);
      var dayEvents = byDay[key] || [];

      var chips = dayEvents
        .map(function (ev) {
          var color = categoryColor(root, ev.category);
          var link = ev.right_link || ev.left_link || source;
          return (
            '<a class="m-dash-events__chip" href="' + esc(link) + '" style="--de-chip: ' + esc(color) + ';" title="' + esc(ev.title) + '">' +
            '<span class="m-dash-events__chip-title">' + esc(ev.title) + '</span>' +
            (ev.time ? '<span class="m-dash-events__chip-time">' + esc(ev.time) + '</span>' : '') +
            '</a>'
          );
        })
        .join('');

      columns.push(
        '<div class="m-dash-events__day' + (i === 0 ? ' is-today' : '') + (dayEvents.length ? '' : ' is-empty') + '">' +
        '<div class="m-dash-events__day-head">' +
        '<span class="m-dash-events__day-name">' + (i === 0 ? 'Today' : DAY_NAMES[date.getDay()]) + '</span>' +
        '<span class="m-dash-events__day-num">' + date.getDate() + '</span>' +
        '</div>' +
        '<div class="m-dash-events__day-body">' + chips + '</div>' +
        '</div>'
      );
    }

    weekEl.innerHTML = columns.join('');

    /* ── List: everything from today onward, soonest first ── */
    var todayKey = dayKey(today);
    var upcoming = events
      .filter(function (ev) {
        return ev.date >= todayKey;
      })
      .sort(function (a, b) {
        return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
      })
      .slice(0, 8);

    listEl.innerHTML = upcoming.length
      ? upcoming
          .map(function (ev) {
            var color = categoryColor(root, ev.category);
            var parts = ev.date.split('-');
            var d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
            var link = ev.right_link || ev.left_link || source;
            return (
              '<a class="m-dash-events__row" href="' + esc(link) + '" style="--de-chip: ' + esc(color) + ';">' +
              '<span class="m-dash-events__row-date">' +
              '<span class="m-dash-events__row-day">' + DAY_NAMES[d.getDay()] + '</span>' +
              '<span class="m-dash-events__row-num">' + d.getDate() + '</span>' +
              '</span>' +
              '<span class="m-dash-events__row-main">' +
              '<span class="m-dash-events__row-title">' + esc(ev.title) + '</span>' +
              (ev.time ? '<span class="m-dash-events__row-time">' + esc(ev.time) + '</span>' : '') +
              '</span>' +
              '</a>'
            );
          })
          .join('')
      : '<p class="m-dash-events__empty">Nothing scheduled yet.</p>';
  }

  function bindToggle(root, weekEl, listEl) {
    var buttons = root.querySelectorAll('[data-de-view]');
    Array.prototype.forEach.call(buttons, function (btn) {
      btn.addEventListener('click', function () {
        var view = btn.getAttribute('data-de-view');
        Array.prototype.forEach.call(buttons, function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        weekEl.hidden = view !== 'week';
        listEl.hidden = view !== 'list';
      });
    });
  }

  function initAll(scope) {
    var container = scope || document;
    var roots = container.querySelectorAll ? container.querySelectorAll('.m-dash-events') : [];
    Array.prototype.forEach.call(roots, initDashEvents);
    if (container.classList && container.classList.contains('m-dash-events')) {
      initDashEvents(container);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
